/**
 * Gate #/review — no queue or identity media until a moderator session is proven.
 */

import { useEffect, useState, type ReactNode } from "react";
import { apiJson, apiHealth } from "../api/client";
import { authStore } from "../auth";

const UNLOCK_KEY = "un8.review.modUnlocked.v1";

function readUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

function writeUnlocked(ok: boolean) {
  try {
    if (ok) sessionStorage.setItem(UNLOCK_KEY, "1");
    else sessionStorage.removeItem(UNLOCK_KEY);
  } catch {
    /* ignore */
  }
}

export function ReviewAuthGate({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [handle, setHandle] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (readUnlocked()) {
        // Re-validate against server when possible so a stale unlock cannot linger.
        if (await apiHealth()) {
          const me = await apiJson<{ user?: { isModerator?: boolean } }>("/api/me", "GET");
          if (me.ok && me.data.user?.isModerator) {
            if (!cancelled) {
              setUnlocked(true);
              setChecking(false);
            }
            return;
          }
          writeUnlocked(false);
        } else if (authStore.isLoggedIn()) {
          // Offline closed-test: session unlock only if already logged in this tab.
          if (!cancelled) {
            setUnlocked(true);
            setChecking(false);
          }
          return;
        }
      }

      if (await apiHealth()) {
        const me = await apiJson<{ user?: { isModerator?: boolean } }>("/api/me", "GET");
        if (me.ok && me.data.user?.isModerator) {
          writeUnlocked(true);
          if (!cancelled) {
            setUnlocked(true);
            setChecking(false);
          }
          return;
        }
      }

      if (!cancelled) {
        setUnlocked(false);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await authStore.login(handle.trim(), passphrase);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      if (await apiHealth()) {
        const me = await apiJson<{ user?: { isModerator?: boolean } }>("/api/me", "GET");
        if (!me.ok || !me.data.user?.isModerator) {
          setError("This account is not a moderator. Review stays locked.");
          writeUnlocked(false);
          return;
        }
      } else {
        // API down: only allow if already authenticated locally — still no public open door.
        if (!authStore.isLoggedIn()) {
          setError("Sign in required. API unreachable — cannot verify moderator.");
          return;
        }
      }
      writeUnlocked(true);
      setUnlocked(true);
      setPassphrase("");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="rc-shell">
        <div className="rc-frame">
          <div className="rc-empty">
            <p>Checking moderator access…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="rc-shell">
        <div className="rc-frame">
          <div className="rc-empty" style={{ maxWidth: 400, margin: "40px auto", textAlign: "left" }}>
            <h1 style={{ fontSize: "1.25rem", marginBottom: 8 }}>Moderator sign-in</h1>
            <p style={{ marginBottom: 16, opacity: 0.85 }}>
              Identity documents and the verification queue are not public. Sign in with a
              moderator account before anything is shown.
            </p>
            <label className="field">
              <span className="field__label">Handle</span>
              <input
                className="field__input"
                autoComplete="username"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
              />
            </label>
            <label className="field" style={{ marginTop: 10 }}>
              <span className="field__label">Passphrase</span>
              <input
                className="field__input"
                type="password"
                autoComplete="current-password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void unlock();
                }}
              />
            </label>
            {error ? (
              <p className="capture__error" role="alert" style={{ marginTop: 12 }}>
                {error}
              </p>
            ) : null}
            <button
              type="button"
              className="rc-toolbar__btn"
              style={{ marginTop: 16 }}
              disabled={busy || handle.trim().length < 2 || passphrase.length < 8}
              onClick={() => void unlock()}
            >
              {busy ? "…" : "Unlock review"}
            </button>
            <p style={{ marginTop: 16 }}>
              <a className="rc-toolbar__link" href="#/">
                ← Back
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
